import { useEffect, useState } from 'react';
import { api, type SiteAssets } from './api.ts';

/**
 * The club's own pictures, fetched once and shared.
 *
 * Deliberately behind the member code, like everything else. That means the
 * sign-in page keeps the built-in look — a stranger who finds the address does
 * not get to see the club's logo before they have a code, which is the same
 * rule the rest of the site follows.
 *
 * Cached at module level rather than re-fetched per screen: this changes about
 * once a year, and three components asking for it on every navigation would be
 * three requests for the same three strings.
 */

const EMPTY: SiteAssets = { logo: null, favicon: null, banner: null, homePhoto1: null, homePhoto2: null, homePhoto3: null };

let cache: SiteAssets | null = null;
let inFlight: Promise<SiteAssets> | null = null;
const listeners = new Set<(assets: SiteAssets) => void>();

function applyFavicon(href: string | null): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!href) return; // Leave whatever the build shipped.
  if (existing) {
    existing.href = href;
    existing.type = 'image/webp';
    return;
  }
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/webp';
  link.href = href;
  document.head.appendChild(link);
}

async function load(): Promise<SiteAssets> {
  if (cache) return cache;
  if (!inFlight) {
    inFlight = api
      .assets()
      .then(({ assets }) => assets)
      // A failure here is cosmetic: the site works perfectly with none of these.
      .catch(() => EMPTY)
      .then((assets) => {
        cache = assets;
        inFlight = null;
        applyFavicon(assets.favicon);
        for (const listener of listeners) listener(assets);
        return assets;
      });
  }
  return inFlight;
}

/** Forgets the cache, so an admin sees their upload without a reload. */
export function siteAssetsChanged(next: SiteAssets): void {
  cache = next;
  applyFavicon(next.favicon);
  for (const listener of listeners) listener(next);
}

export function useSiteAssets(): SiteAssets {
  const [assets, setAssets] = useState<SiteAssets>(() => cache ?? EMPTY);

  useEffect(() => {
    listeners.add(setAssets);
    void load().then(setAssets);
    return () => {
      listeners.delete(setAssets);
    };
  }, []);

  return assets;
}
